import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/format";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

/* ── Types ── */
interface Snapshot {
  date: string;
  nav: number;
  benchmarkNav?: number;
}

interface SnapshotsResponse {
  data: Snapshot[];
  total: number;
}

/* ── Tooltip ── */
function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#2b2b2b",
        border: "1px solid #444",
        borderRadius: 6,
        padding: "8px 12px",
        fontSize: 13,
      }}
    >
      <div style={{ color: "#aaa", marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: {formatINR(p.value)}
        </div>
      ))}
    </div>
  );
}

/* ── Component ── */
export default function EquityCurveChart() {
  const { data, isLoading, isError } = useQuery<SnapshotsResponse>({
    queryKey: ["portfolio-snapshots"],
    queryFn: () => api.get<SnapshotsResponse>("/portfolio/snapshots"),
    refetchInterval: 60_000,
  });

  const snapshots = data?.data ?? [];

  /* Empty / loading / error states */
  if (isLoading) {
    return (
      <div className="card" style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "var(--text-muted)" }}>Loading equity curve…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card" style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "var(--red-light)" }}>Failed to load snapshots</span>
      </div>
    );
  }

  if (snapshots.length === 0) {
    return (
      <div className="card" style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "var(--text-muted)" }}>
          No data yet — start trading to see equity curve
        </span>
      </div>
    );
  }

  const latest = snapshots[snapshots.length - 1];
  const hasBenchmark = snapshots.some((s) => s.benchmarkNav != null);

  /* Format date labels compactly */
  const chartData = snapshots.map((s) => ({
    ...s,
    dateLabel: new Date(s.date).toLocaleDateString("en-IN", {
      month: "short",
      day: "2-digit",
    }),
  }));

  return (
    <div className="card" style={{ minHeight: 280 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.05 }}>
          Equity Curve
        </h3>
        <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "var(--font-mono)" }}>
          {formatINR(latest.nav)}
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={chartData} margin={{ top: 5, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" />
          <XAxis
            dataKey="dateLabel"
            tick={{ fill: "#999", fontSize: 11 }}
            axisLine={{ stroke: "#444" }}
            tickLine={{ stroke: "#444" }}
          />
          <YAxis
            tick={{ fill: "#999", fontSize: 11 }}
            axisLine={{ stroke: "#444" }}
            tickLine={{ stroke: "#444" }}
            tickFormatter={(v: number) => `₹${(v / 1000).toFixed(1)}k`}
            domain={["auto", "auto"]}
          />
          <Tooltip content={<CustomTooltip />} />
          {hasBenchmark && (
            <Legend
              wrapperStyle={{ fontSize: 12, color: "#ccc" }}
              iconType="line"
            />
          )}
          <Line
            type="monotone"
            dataKey="nav"
            name="Strategy NAV"
            stroke="#6C5CE7"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
          />
          {hasBenchmark && (
            <Line
              type="monotone"
              dataKey="benchmarkNav"
              name="Benchmark Nifty 50"
              stroke="#a8e6cf"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
              strokeDasharray="6 3"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
